import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useGetDiscussionsQuery } from '../store/api/discussionsApi'
import { useGetCategoriesQuery } from '../store/api/categoriesApi'
import { useGetPreferencesQuery, useUpdatePreferencesMutation } from '../store/api/usersApi'
import { useAppSelector } from '../hooks/useAppDispatch'
import DiscussionCard from '../components/DiscussionCard'
import { MultiSelectDropdown } from '../components/ui/MultiSelectDropdown'
import { EmptyState, IconButton, SkeletonCard, RefreshButton, FilterChip } from '../components/ui'
import s from '../styles/pages/SearchPage.module.css'
import { DISCUSSIONS_PAGE_SIZE } from '../lib/config'
import { contentPreview } from '../lib/avatar'
import { useInfiniteScroll } from '../hooks/useInfiniteScroll'

const SORT_OPTIONS = [
  { key: 'new', label: '🕐 Latest' },
  { key: 'hot', label: '🔥 Hot' },
  { key: 'top', label: '⬆️ Top' },
]

const DATE_PRESETS = [
  { key: '',       label: 'Any time' },
  { key: 'today',  label: 'Today' },
  { key: 'week',   label: 'This week' },
  { key: 'month',  label: 'This month' },
  { key: 'year',   label: 'This year' },
  { key: 'custom', label: 'Custom…' },
]

const MIN_VOTES_OPTIONS = [
  { key: '',   label: 'Any' },
  { key: '1',  label: '1+' },
  { key: '5',  label: '5+' },
  { key: '10', label: '10+' },
  { key: '25', label: '25+' },
]

function getDateRange(preset: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date()
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  switch (preset) {
    case 'today': return { dateFrom: fmt(now) }
    case 'week':  { const d = new Date(now); d.setDate(d.getDate() - 7);        return { dateFrom: fmt(d) } }
    case 'month': { const d = new Date(now); d.setMonth(d.getMonth() - 1);      return { dateFrom: fmt(d) } }
    case 'year':  { const d = new Date(now); d.setFullYear(d.getFullYear() - 1); return { dateFrom: fmt(d) } }
    default: return {}
  }
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [cursor, setCursor]             = useState<string | undefined>(undefined)
  const [showFilters, setShowFilters]   = useState(false)
  const [datePreset, setDatePreset]     = useState('')
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo,   setCustomDateTo]   = useState('')

  const q              = searchParams.get('q')        ?? ''
  const sortParam      = searchParams.get('sort')     ?? 'new'
  const rawCategories  = searchParams.get('categorySlugs')
  const categorySlugs  = rawCategories ? rawCategories.split(',').filter(Boolean)
    : searchParams.get('category') ? [searchParams.get('category')!] : []  // back-compat
  const tagParam       = searchParams.get('tag')      ?? undefined
  const minVotesParam  = searchParams.get('minVotes') ?? ''
  const dateFromParam  = searchParams.get('dateFrom') ?? ''
  const dateToParam    = searchParams.get('dateTo')   ?? ''

  const [sort, setSort] = useState(sortParam)

  const { data: categoriesData } = useGetCategoriesQuery()
  const categories = categoriesData?.categories ?? []

  const { isAuthenticated } = useAppSelector((s) => s.auth)
  const { data: prefsData } = useGetPreferencesQuery(undefined, { skip: !isAuthenticated })
  const [updatePreferences, { isLoading: isUpdatingPrefs }] = useUpdatePreferencesMutation()
  const followedTags: string[] = prefsData?.preferences?.followedTags ?? []
  const isTagFollowed = !!tagParam && followedTags.includes(tagParam)

  // Sync custom date inputs from URL on mount
  useEffect(() => {
    if (dateFromParam || dateToParam) {
      setDatePreset('custom')
      setCustomDateFrom(dateFromParam)
      setCustomDateTo(dateToParam)
    }
  }, [])

  const handleToggleTagFollow = async () => {
    if (!tagParam || isUpdatingPrefs) return
    const updated = isTagFollowed
      ? followedTags.filter(t => t !== tagParam)
      : [...followedTags, tagParam]
    await updatePreferences({ followedTags: updated }).catch(() => {})
  }

  const activeDateFrom = datePreset === 'custom' ? customDateFrom : (getDateRange(datePreset).dateFrom ?? '')
  const activeDateTo   = datePreset === 'custom' ? customDateTo   : (getDateRange(datePreset).dateTo   ?? '')

  const hasQuery = !!q || !!tagParam || categorySlugs.length > 0

  const handleCategoryChange = (slugs: string[]) => {
    setCursor(undefined)
    const next = new URLSearchParams(searchParams)
    next.delete('categorySlugs')
    next.delete('category')
    if (slugs.length > 0) next.set('categorySlugs', slugs.join(','))
    setSearchParams(next, { replace: true })
  }

  const { data, isLoading, isFetching, refetch } = useGetDiscussionsQuery(
    {
      search: q || undefined,
      cursor,
      limit: DISCUSSIONS_PAGE_SIZE,
      sort,
      ...(categorySlugs.length === 1 && { categorySlug: categorySlugs[0] }),
      ...(categorySlugs.length > 1  && { categorySlugs: categorySlugs.join(',') }),
      ...(tagParam       && { tag: tagParam }),
      ...(minVotesParam  && { minVotes: parseInt(minVotesParam) }),
      ...(activeDateFrom && { dateFrom: activeDateFrom }),
      ...(activeDateTo   && { dateTo:   activeDateTo }),
    },
    { skip: !q && !tagParam && categorySlugs.length === 0 }
  )

  const discussions = hasQuery ? (data?.data ?? []) : []

  useEffect(() => { setCursor(undefined) }, [q, sort, categorySlugs.join(','), tagParam, minVotesParam, dateFromParam, dateToParam])

  const loadMore = useCallback(() => {
    if (data?.hasMore && data.nextCursor) setCursor(data.nextCursor)
  }, [data])

  const sentinelRef = useInfiniteScroll({ hasMore: data?.hasMore ?? false, isFetching, onLoadMore: loadMore })

  const updateFilter = (key: string, value: string | undefined) => {
    setCursor(undefined)
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value); else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const applyDatePreset = (preset: string) => {
    setDatePreset(preset)
    if (preset === 'custom') return
    const next = new URLSearchParams(searchParams)
    const range = getDateRange(preset)
    if (range.dateFrom) next.set('dateFrom', range.dateFrom); else next.delete('dateFrom')
    if (range.dateTo)   next.set('dateTo',   range.dateTo);   else next.delete('dateTo')
    setSearchParams(next, { replace: true })
  }

  const applyCustomDates = () => {
    const next = new URLSearchParams(searchParams)
    if (customDateFrom) next.set('dateFrom', customDateFrom); else next.delete('dateFrom')
    if (customDateTo)   next.set('dateTo',   customDateTo);   else next.delete('dateTo')
    setSearchParams(next, { replace: true })
  }

  const clearDateFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('dateFrom')
    next.delete('dateTo')
    setSearchParams(next, { replace: true })
    setDatePreset('')
    setCustomDateFrom('')
    setCustomDateTo('')
  }

  const clearFilters = () => {
    const next = new URLSearchParams()
    if (q) next.set('q', q)
    setSearchParams(next, { replace: true })
    setSort('new')
    setDatePreset('')
    setCustomDateFrom('')
    setCustomDateTo('')
    setShowFilters(false)
    setCursor(undefined)
  }

  const hasFilters = sort !== 'new' || categorySlugs.length > 0 || !!tagParam || !!minVotesParam || !!dateFromParam || !!dateToParam

  const activeFilterCount = [
    sort !== 'new',
    categorySlugs.length > 0,
    !!minVotesParam,
    !!(dateFromParam || dateToParam),
  ].filter(Boolean).length

  return (
    <div>
      <div className={s.stickyHeader}>
        <div className={s.headerRow}>
          <Link to="/" className={s.backLink}>← Back</Link>
          <h2 className={s.headerTitle}>
            {tagParam ? `#️⃣ #${tagParam}` : q ? `🔍 "${q}"` : '🔍 Search'}
          </h2>
          <div className={s.headerActions}>
            {hasQuery && (
              <button
                onClick={() => setShowFilters(v => !v)}
                className={`${s.filtersBtn} ${showFilters || activeFilterCount > 0 ? s.active : ''}`}
              >
                🎛️ Filters
                {activeFilterCount > 0 && <span className={s.filterBadge}>{activeFilterCount}</span>}
              </button>
            )}
            {hasQuery && (
              <RefreshButton
                onClick={() => { setCursor(undefined); void refetch() }}
                loading={isFetching}
              />
            )}
          </div>
        </div>
        {hasQuery && !isLoading && (
          <div className={s.resultsCount}>
            {discussions.length === 0
              ? 'No results found'
              : `${discussions.length}${data?.hasMore ? '+' : ''} discussion${discussions.length === 1 ? '' : 's'} found`}
          </div>
        )}
      </div>

      {hasQuery && showFilters && (
        <div className={s.filterPanel}>
          <div className={s.filterRow}>
            <span className={s.filterLabel}>Sort by</span>
            {SORT_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => { setSort(opt.key); updateFilter('sort', opt.key === 'new' ? undefined : opt.key) }} className={`${s.chip} ${sort === opt.key ? s.active : ''}`}>
                {opt.label}
              </button>
            ))}
          </div>

          <div className={s.filterRow}>
            <span className={s.filterLabel}>Topic</span>
            <MultiSelectDropdown
              options={categories.filter(c => c.slug !== 'general').map(c => ({ value: c.slug, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
              selected={categorySlugs}
              onChange={handleCategoryChange}
              placeholder="All topics"
              searchPlaceholder="Search topics..."
              align="left"
            />
            {categorySlugs.map(slug => {
              const cat = categories.find(c => c.slug === slug)
              return (
                <span key={slug} className={s.catChip} style={{ background: `${cat?.color ?? 'var(--accent-primary)'}18`, border: `1px solid ${cat?.color ?? 'var(--accent-primary)'}55`, color: cat?.color ?? 'var(--accent-primary)' }}>
                  {cat?.icon && <span className={s.catChipIcon}>{cat.icon}</span>}
                  {cat?.name ?? slug}
                  <button onClick={() => handleCategoryChange(categorySlugs.filter(sl => sl !== slug))} className={s.catChipRemove}>✕</button>
                </span>
              )
            })}
          </div>

          <div className={s.filterRow}>
            <span className={s.filterLabel}>Date</span>
            {DATE_PRESETS.map(opt => (
              <button key={opt.key} onClick={() => applyDatePreset(opt.key)} className={`${s.chip} ${datePreset === opt.key ? s.active : ''}`}>
                {opt.label}
              </button>
            ))}
            {datePreset === 'custom' && (
              <div className={s.dateInputs}>
                <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} className={s.dateInput} />
                <span className={s.dateSeparator}>to</span>
                <input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} className={s.dateInput} />
                <button onClick={applyCustomDates} className={s.applyBtn}>Apply</button>
              </div>
            )}
          </div>

          <div className={s.filterRow}>
            <span className={s.filterLabel}>Min votes</span>
            {MIN_VOTES_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => updateFilter('minVotes', opt.key || undefined)} className={`${s.chip} ${minVotesParam === opt.key || (!minVotesParam && opt.key === '') ? s.active : ''}`}>
                {opt.label}
              </button>
            ))}
          </div>

          {tagParam && (
            <div className={s.filterRow}>
              <span className={s.filterLabel}>Tag</span>
              <div className={s.tagPill}>
                #{tagParam}
                <button onClick={() => updateFilter('tag', undefined)} className={s.tagPillRemove}>✕</button>
              </div>
              {isAuthenticated && (
                <button
                  onClick={() => void handleToggleTagFollow()}
                  disabled={isUpdatingPrefs}
                  className={`${s.tagFollowBtn} ${isTagFollowed ? s.followed : ''}`}
                  style={{ opacity: isUpdatingPrefs ? 0.6 : 1, cursor: isUpdatingPrefs ? 'not-allowed' : 'pointer' }}
                >
                  {isTagFollowed ? '✓ Following' : '+ Follow'}
                </button>
              )}
            </div>
          )}

          {hasFilters && (
            <div className={s.filterFooter}>
              <div className={s.filterFooterChips}>
                {sort !== 'new' && <FilterChip label={SORT_OPTIONS.find(o => o.key === sort)?.label ?? sort} onRemove={() => { setSort('new'); updateFilter('sort', undefined) }} />}
                {categorySlugs.map(slug => {
                  const cat = categories.find(c => c.slug === slug)
                  return <FilterChip key={slug} label={`${cat?.icon ?? ''} ${cat?.name ?? slug}`.trim()} onRemove={() => handleCategoryChange(categorySlugs.filter(sl => sl !== slug))} />
                })}
                {(dateFromParam || dateToParam) && <FilterChip label={`📅 ${dateFromParam || '…'} → ${dateToParam || '…'}`} onRemove={clearDateFilter} />}
                {minVotesParam && <FilterChip label={`👍 ${minVotesParam}+ votes`} onRemove={() => updateFilter('minVotes', undefined)} />}
              </div>
              <button onClick={clearFilters} className={s.clearAllBtn}>✕ Clear all</button>
            </div>
          )}
        </div>
      )}

      {hasQuery && !showFilters && hasFilters && (
        <div className={s.activeChipsBar}>
          {sort !== 'new' && <FilterChip label={SORT_OPTIONS.find(o => o.key === sort)?.label ?? sort} onRemove={() => { setSort('new'); updateFilter('sort', undefined) }} />}
          {categorySlugs.map(slug => {
            const cat = categories.find(c => c.slug === slug)
            return <FilterChip key={slug} label={`${cat?.icon ?? ''} ${cat?.name ?? slug}`.trim()} onRemove={() => handleCategoryChange(categorySlugs.filter(sl => sl !== slug))} />
          })}
          {tagParam && <FilterChip label={`#${tagParam}`} onRemove={() => updateFilter('tag', undefined)} />}
          {(dateFromParam || dateToParam) && <FilterChip label={`📅 ${dateFromParam || '…'} → ${dateToParam || '…'}`} onRemove={clearDateFilter} />}
          {minVotesParam && <FilterChip label={`👍 ${minVotesParam}+ votes`} onRemove={() => updateFilter('minVotes', undefined)} />}
        </div>
      )}

      {/* Loading skeletons */}
      {(isLoading || isFetching) && !discussions.length && (
        <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
      )}

      {/* No query state */}
      {!hasQuery && (
        <EmptyState
          icon="🔍"
          title="Search discussions"
          description="Use the search bar above to find discussions, or type #tag to search by hashtag"
        />
      )}

      {/* Empty results */}
      {!isLoading && !isFetching && hasQuery && discussions.length === 0 && (
        <EmptyState
          icon={tagParam ? '#️⃣' : '🔍'}
          title="No results found"
          description={tagParam ? `No discussions tagged with #${tagParam}` : hasFilters ? 'Try removing some filters' : 'Try different keywords or browse by category'}
          action={hasFilters ? <button onClick={clearFilters} style={{ padding: '0.45rem 1rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Clear filters</button> : undefined}
        />
      )}

      {/* Results */}
      {discussions.map((d) => (
        <DiscussionCard
          key={d.id} id={d.id} title={d.title}
          contentPreview={contentPreview(d.content)}
          author={{ id: d.author.id, displayName: d.author.profile?.displayName ?? 'User', avatarUrl: d.author.profile?.avatarUrl, flair: d.author.profile?.flair }}
          categories={d.categories}
          tags={d.tags.map((t) => t.tag.name)}
          viewCount={d.viewCount} commentCount={d.commentCount} voteScore={d.voteScore}
          createdAt={d.createdAt} isPinned={d.isPinned}
        />
      ))}

      <div ref={sentinelRef} className={s.sentinel}>
        {isFetching && discussions.length > 0 && <span className={s.sentinelText}>Loading more…</span>}
      </div>
    </div>
  )
}

// ── Active filter chip ─────────────────────────────────────────

